import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Globe, Lock, MessageCircle, Crown, ChevronRight,
  UserPlus, Check, Clock, Trash2,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import getSportEmoji from '../../utils/sportEmoji';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import ConfirmModal from '../../components/ConfirmModal';
import { btn } from '../../utils/buttonStyles';
import { isSquadManager, roleLabel } from '../utils/squadRoles';

/**
 * SquadCard - Self-contained Squad Card Component
 *
 * Props:
 * - squad: Squad object with all data
 * - sportTypes: Array of sport types for lookup
 * - onJoin: Callback when join button is clicked (squad) => void
 * - onLeave: Callback when leave button is clicked (squad) => void
 * - onAcceptInvitation: Callback when accept invitation is clicked (squad) => void
 */
const SquadCard = ({ squad, sportTypes = [], onJoin, onLeave, onAcceptInvitation, onView, onRemove, hideLeave = false }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);

  // Get sport name — falls back to squad.sportName if sportTypeId lookup fails
  const getSportName = (sportTypeId) => {
    if (!sportTypeId) return squad.sportName || 'General';
    const sport = sportTypes.find(s => s.id === sportTypeId);
    return sport?.sportName || squad.sportName || 'General';
  };

  const sportName = getSportName(squad.sportTypeId);

  // Get creator display name
  const getCreatorName = () => {
    if (squad.creatorFirstName && squad.creatorLastName) {
      return `${squad.creatorFirstName} ${squad.creatorLastName}`;
    }
    if (squad.creatorFirstName) {
      return squad.creatorFirstName;
    }
    return squad.creatorUsername || 'Unknown';
  };

  // Render squad icon (image, emoji, or initial)
  const renderIcon = () => {
    if (squad.backgroundImage) {
      return (
        <img
          src={resolveImageUrl(squad.backgroundImage)}
          alt={squad.name}
          className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
        />
      );
    }
    if (squad.sportTypeId) {
      const emoji = getSportEmoji(sportName);
      return (
        <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-base flex-shrink-0">
          {emoji}
        </span>
      );
    }
    return (
      <span className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-600 flex-shrink-0">
        {(squad.name || '?')[0].toUpperCase()}
      </span>
    );
  };

  // Handle view squad — caller can override to prevent navigation
  const handleViewSquad = () => {
    if (onView) { onView(squad); return; }
    navigate(`/squads/${squad.id}`);
  };

  // Handle join squad
  const handleJoin = (e) => {
    e.stopPropagation();
    if (onJoin) onJoin(squad);
  };

  // Handle leave squad - show modal
  const handleLeave = (e) => {
    e.stopPropagation();
    setShowLeaveModal(true);
  };

  // Confirm leave squad
  const confirmLeave = async () => {
    if (onLeave) {
      setLeaveLoading(true);
      await onLeave(squad);
      setLeaveLoading(false);
      setShowLeaveModal(false);
    }
  };

  // Handle accept invitation
  const handleAccept = (e) => {
    e.stopPropagation();
    if (onAcceptInvitation) onAcceptInvitation(squad);
  };

  return (
    <>
      {/* Border comes from theme.card alone — repeating `border` here reset the
          width back to 1px and fought the token's border-2. */}
      <div className={`${theme.card} rounded-2xl p-6 hover:shadow-lg transition-all duration-300 h-full flex flex-col`}>
      <div className="flex items-start justify-between mb-4 flex-1">
        <div className="flex-1 flex gap-3">
          {renderIcon()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-x-2 mb-2">
              <h3 className={`font-bold text-lg ${theme.text}`}>{squad.name}</h3>
              {squad.privacy === 'private' ? (
                <Lock className="w-4 h-4 text-gray-500" />
              ) : (
                <Globe className="w-4 h-4 text-green-500" />
              )}
            </div>

            <p className={`${theme.textSecondary} text-sm mb-3 line-clamp-2 min-h-[2.5rem]`}>
              {squad.description || 'No description available'}
            </p>

            <div className={`flex items-center flex-wrap gap-2 text-xs ${theme.textSecondary} mb-4`}>
              <div className="flex items-center gap-x-1">
                <Users className="w-3 h-3" />
                <span>{squad.memberCount} {squad.memberCount === 1 ? 'member' : 'members'}</span>
              </div>
              <div className="flex items-center gap-x-1">
                <MessageCircle className="w-3 h-3" />
                <span>{squad.postCount || 0} posts</span>
              </div>
              {squad.privacy === 'private' ? (
                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs flex items-center gap-x-1">
                  <Lock className="w-3 h-3" />
                  <span>Private</span>
                </span>
              ) : (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-x-1">
                  <Globe className="w-3 h-3" />
                  <span>Public</span>
                </span>
              )}
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                {sportName}
              </span>
            </div>

            <p className={`text-xs ${theme.textMuted}`}>
              Created by {getCreatorName()} • {new Date(squad.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-700/30">
        <div className="flex items-center gap-x-2">
          {squad.userRole && (
            <span className={`px-2 py-1 text-xs rounded-full ${
              isSquadManager(squad.userRole)
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-blue-100 text-blue-800'
            }`}>
              {isSquadManager(squad.userRole) ? (
                <><Crown className="w-3 h-3 inline mr-1" />{roleLabel(squad.userRole)}</>
              ) : (
                roleLabel(squad.userRole)
              )}
            </span>
          )}
        </div>

        <div className={btn.group}>
          {onRemove && (
            <button
              onClick={e => { e.stopPropagation(); onRemove(squad); }}
              className={`${btn.base} ${btn.md} ${theme.buttonDanger} flex items-center gap-x-1`}
              title="Remove from program"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
          {squad.isMember ? (
            <>
              {/* Show Accept button if user has invited status (admin invitation) */}
              {squad.memberStatus === 'invited' ? (
                <button
                  onClick={handleAccept}
                  className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-1`}
                >
                  <Check className="w-3 h-3" />
                  <span>Accept</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={handleViewSquad}
                    className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-1`}
                  >
                    <span>View</span>
                    <ChevronRight className="w-3 h-3" />
                  </button>

                  {!hideLeave && !isSquadManager(squad.userRole) && (
                    <button
                      onClick={handleLeave}
                      className={`${btn.base} ${btn.md} ${theme.buttonDanger}`}
                    >
                      Leave
                    </button>
                  )}
                </>
              )}
            </>
          ) : squad.memberStatus === 'invited' ? (
            // Show Accept button for invitations
            <button
              onClick={handleAccept}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-1`}
            >
              <Check className="w-3 h-3" />
              <span>Accept</span>
            </button>
          ) : squad.memberStatus === 'pending' ? (
            // Show Request Pending for pending join requests
            <button
              disabled
              className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium flex items-center gap-x-1 cursor-not-allowed"
            >
              <Clock className="w-3 h-3" />
              <span>Request Pending</span>
            </button>
          ) : (
            <button
              onClick={handleJoin}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-1`}
            >
              <UserPlus className="w-3 h-3" />
              <span>Join</span>
            </button>
          )}
        </div>
      </div>
      </div>

      {/* Leave Squad Confirm Modal */}
      <ConfirmModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onConfirm={confirmLeave}
        title="Leave Squad"
        message={`Are you sure you want to leave "${squad.name}"? You may need to be re-invited to rejoin.`}
        confirmText="Leave"
        confirmStyle="danger"
        loading={leaveLoading}
      />
    </>
  );
};

export default SquadCard;
